import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  AgentConversation,
  AgentMessage,
  AgentRunDetail,
  agentRunQuery,
} from "@/app/Agents/GraphQL/agentRunQueries";
import { AGENT_RUN_STATE_TONE } from "@/app/Agents/Utils/agentRunMaps";
import { Badge } from "@/components/catalyst/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AgentRunKind } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import { ReactNode, useState } from "react";
import dayjs from "dayjs";

export const AgentRunDialog: DialogComponent<{
  kind: AgentRunKind;
  id: string;
}> = ({ kind, id }) => {
  const { data, isLoading } = useGqlQuery(agentRunQuery, { kind, id });

  const run = data?.agentRun;

  return (
    <BaseDialog
      title={run?.title ?? "Agent run"}
      subtitle={
        run
          ? `${run.subtitle ?? ""}${run.subtitle ? " · " : ""}${dayjs(run.created_at).format("D MMM YYYY, h:mma")}`
          : undefined
      }
      fullScreen
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        {isLoading ? <Skeleton className="h-20 w-full" /> : null}

        {run ? <Outcome run={run} /> : null}
        {run ? <Log run={run} /> : null}

        {run && run.transcript.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
            No agent transcript for this run. What the automation recorded doing
            is listed above.
          </p>
        ) : null}

        {run?.transcript.map((conversation) => (
          <Conversation key={conversation.id} conversation={conversation} />
        ))}
      </div>
    </BaseDialog>
  );
};

function Outcome({ run }: { run: AgentRunDetail }) {
  return (
    <div className="shrink-0 space-y-2 rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={AGENT_RUN_STATE_TONE[run.state]}>
          {run.status.replaceAll("_", " ").toLowerCase()}
        </Badge>
        {run.facts.map((fact) => (
          <span key={fact.label} className="text-xs text-gray-500">
            {fact.label}: <span className="text-gray-900">{fact.value}</span>
          </span>
        ))}
      </div>
      {run.note ? (
        <p className="whitespace-pre-wrap text-sm text-gray-900">{run.note}</p>
      ) : null}
      {run.failure_reason ? (
        <p className="whitespace-pre-wrap text-sm text-red-600">
          {run.failure_reason}
        </p>
      ) : null}
    </div>
  );
}

function Log({ run }: { run: AgentRunDetail }) {
  return run.log.length === 0 ? null : (
    <div className="space-y-2">
      <p className="text-xs font-medium text-gray-600">What it did</p>
      <ol className="space-y-1">
        {run.log.map((entry, index) => (
          <li
            key={`${entry.label}-${index}`}
            className="flex gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <span
              className={cn(
                "mt-1.5 size-1.5 shrink-0 rounded-full",
                entry.ok ? "bg-green-500" : "bg-amber-500",
              )}
            />
            <div className="min-w-0">
              <p className="font-mono text-xs font-semibold text-indigo-700">
                {entry.label}
              </p>
              <p className="whitespace-pre-wrap text-sm text-gray-800">
                {entry.detail}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Conversation({ conversation }: { conversation: AgentConversation }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-t border-gray-200 pt-4">
        <p className="text-xs font-semibold text-gray-700">
          Run {conversation.run_number}
        </p>
        <span className="text-xs text-gray-400">
          {dayjs(conversation.started_at).format("D MMM YYYY, h:mma")}
        </span>
      </div>
      {conversation.messages.map((message) => (
        <Message key={message.id} message={message} />
      ))}
    </div>
  );
}

function Message({ message }: { message: AgentMessage }) {
  const isAssistant = message.role === "assistant";

  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-2 text-xs text-zinc-500">
        <span className="font-medium text-zinc-600">
          {isAssistant ? "Agent" : "Prompt"}
        </span>
        {message.model ? (
          <span className="font-mono text-[11px]">{message.model}</span>
        ) : null}
        {message.prompt_tokens !== null &&
        message.completion_tokens !== null ? (
          <span>
            {message.prompt_tokens} in / {message.completion_tokens} out
          </span>
        ) : null}
      </p>

      {message.content ? (
        <div
          className={cn(
            "whitespace-pre-wrap rounded-xl px-4 py-3 text-sm",
            isAssistant
              ? "border border-l-2 border-zinc-950/5 border-l-indigo-500/60 bg-zinc-50 text-zinc-900"
              : "bg-zinc-100 text-zinc-900",
          )}
        >
          {message.content}
        </div>
      ) : null}

      {message.steps.map((step) => (
        <ToolCallCard
          key={step.id}
          tool={step.tool}
          argumentsJson={step.arguments_json}
          result={step.result}
          denied={step.denied}
        />
      ))}
    </div>
  );
}

function ToolCallCard({
  tool,
  argumentsJson,
  result,
  denied,
}: {
  tool: string;
  argumentsJson: string;
  result: string | null;
  denied: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const badge: ReactNode = denied ? (
    <Badge color="red" className="py-0">
      denied
    </Badge>
  ) : null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span className="font-mono text-xs font-semibold text-indigo-700">
          {tool}
        </span>
        {badge}
        <span className="ml-auto text-xs text-gray-400">
          {isOpen ? "Hide" : "Show"}
        </span>
      </button>

      {isOpen ? (
        <div className="space-y-2 border-t border-gray-100 px-3 py-2">
          <div>
            <p className="text-[11px] font-medium text-gray-500">Arguments</p>
            <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-2 font-mono text-[11px] text-gray-800">
              {argumentsJson}
            </pre>
          </div>
          {result !== null ? (
            <div>
              <p className="text-[11px] font-medium text-gray-500">Result</p>
              <p className="mt-1 whitespace-pre-wrap text-xs text-gray-800">
                {result}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
